/**
 * PreprintSettingsSection — per-location pre-print permission toggle (BLU-717 S3).
 *
 * A pre-print run writes the design SKU into the catalog as PAP and mints its
 * stock row at the run's location. Restricting WHERE runs can be created is the
 * creation-side half of the BLU-717 fix ("HQ is the only location that
 * pre-prints"): S2 made consumption resolve per-location; this stops a run —
 * or a manual pre-printed stock add — from being created at a ledger-only
 * location in the first place. Default OFF; locations with real pre-print
 * history were grandfathered ON by migration 20260730160000.
 *
 * Mirrors OrderHoldSettingsSection's structure (embedded + locationId props,
 * getLocationSettings query, per-row mutate with a variables-keyed pending
 * spinner, toast + refetch) with ONE deliberate deviation: the row carries TWO
 * peer switches (garments / films) instead of one. They share a row rather than
 * stacking so the switch columns line up vertically across locations — the
 * question a coordinator asks here is "which floors pre-print?", which is read
 * down a column. The row wraps below ~sm so the two controls never squeeze the
 * location name (see the cluster comment inline).
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { NoteLine } from '@/components/shared/NoteLine';
import { MapPin, Loader2, AlertTriangle, RefreshCw, Layers, ExternalLink } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface PreprintSettingsSectionProps {
  /** When true, renders without the Card wrapper for embedding in the Console. */
  embedded?: boolean;
  /** Console single-location mode: render only this location's row. */
  locationId?: string;
}

export function PreprintSettingsSection({
  embedded = false,
  locationId,
}: PreprintSettingsSectionProps) {
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();

  // In full-list mode a bare "for this location" toast is ambiguous (five rows,
  // one message), so every confirmation names the floor it changed.
  const nameOf = (id: string | undefined) =>
    locationsQuery.data?.find((l) => l.id === id)?.name ?? 'this location';

  const setPreprintMutation = trpc.productionSettings.setLocationPreprintEnabled.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(
        `Garment pre-printing ${variables.enabled ? 'enabled' : 'disabled'} at ${nameOf(variables.locationId)}`
      );
      locationsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update garment pre-print permission: ${error.message}`);
    },
  });

  // Separate key on purpose — a QA'd film is a physical object at ONE location,
  // and neither capability (garment vs film pre-printing) inherits the other.
  const setFilmPreprintMutation = trpc.productionSettings.setLocationFilmPreprintEnabled.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(
        `Stock film pre-printing ${variables.enabled ? 'enabled' : 'disabled'} at ${nameOf(variables.locationId)}`
      );
      locationsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to update film pre-print permission: ${error.message}`);
    },
  });

  // One live region serves both switches, so its text must name WHICH capability
  // moved and where — otherwise the second toggle re-announces an identical
  // sentence and tells the operator nothing. Newest success wins.
  const garmentDoneAt = setPreprintMutation.isSuccess ? setPreprintMutation.submittedAt : 0;
  const filmDoneAt = setFilmPreprintMutation.isSuccess ? setFilmPreprintMutation.submittedAt : 0;
  const lastChange =
    filmDoneAt > garmentDoneAt
      ? { label: 'Stock film pre-printing', variables: setFilmPreprintMutation.variables }
      : garmentDoneAt > 0
        ? { label: 'Garment pre-printing', variables: setPreprintMutation.variables }
        : null;
  const liveMessage = lastChange?.variables
    ? `${lastChange.label} ${lastChange.variables.enabled ? 'enabled' : 'disabled'} at ${nameOf(lastChange.variables.locationId)}`
    : '';

  const isLoading = locationsQuery.isLoading;
  const hasError = locationsQuery.isError;
  const allLocations = locationsQuery.data || [];
  const locations = locationId ? allLocations.filter((l) => l.id === locationId) : allLocations;

  const content = (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Choose which locations can create pre-print runs, add pre-printed stock, or print stock
        films. Pre-printed garments and QA'd films are physical objects that exist at exactly one
        location, so each capability is granted per location. Keep both off for locations that
        only track inventory.
      </p>

      {/* Error State */}
      {hasError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Failed to load locations</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {locationsQuery.error?.message || 'Unknown error'}
            <Button variant="link" className="p-0 h-auto" onClick={() => locationsQuery.refetch()}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="ml-2 text-muted-foreground">Loading locations...</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !hasError && locations.length === 0 && (
        <div className="flex flex-col items-center py-8 text-center bg-muted/30 rounded-lg">
          <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" />
          <p className="font-medium">No locations configured</p>
          <p className="text-sm text-muted-foreground mb-4">
            Add locations in IMS Settings to configure pre-printing
          </p>
          <Button variant="outline" asChild className="h-12 gap-2">
            <a href="/settings?tab=locations">
              <ExternalLink className="h-4 w-4" />
              Go to IMS Settings
            </a>
          </Button>
        </div>
      )}

      {/* Location List */}
      {!isLoading && !hasError && locations.length > 0 && (
        <div className="space-y-3" role="list" aria-label="Per-location garment and film pre-print permissions">
          {locations.map((location) => {
            const enabled = location.preprintEnabled;
            const filmEnabled = location.filmPreprintEnabled;
            const rowPending =
              setPreprintMutation.isPending &&
              setPreprintMutation.variables?.locationId === location.id;
            const filmRowPending =
              setFilmPreprintMutation.isPending &&
              setFilmPreprintMutation.variables?.locationId === location.id;
            const switchId = `preprint-enabled-${location.id}`;
            const filmSwitchId = `film-preprint-enabled-${location.id}`;
            // Four literal states, not an assembled string: a half-on floor has
            // to say what is BLOCKED too (the assembled version named only what
            // was allowed, so "Stock films allowed" read as a full permission),
            // and each sentence stays translatable as a unit.
            const summary = enabled
              ? filmEnabled
                ? 'Garment pre-prints and stock films allowed'
                : 'Garment pre-prints allowed, stock films blocked'
              : filmEnabled
                ? 'Stock films allowed, garment pre-prints blocked'
                : 'Pre-printing blocked at this location';
            return (
              <div key={location.id} role="listitem" className="overflow-hidden rounded-lg bg-muted/30">
                {/* Wraps on purpose (the sibling one-switch rows do not): two
                    labelled switches are ~240px, so on the Console's narrow
                    column the cluster drops to its own line instead of
                    crushing the location name. */}
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-3 transition-colors hover:bg-muted/50">
                  <div className="min-w-0">
                    {!locationId && <p className="font-medium">{location.name}</p>}
                    <NoteLine icon={Layers} className="mt-1">
                      {summary}
                    </NoteLine>
                  </div>

                  {/* Per-switch disabled scope is deliberate: the two keys are
                      written by separate single-statement JSONB merges under the
                      same row lock, so a concurrent garment + film toggle cannot
                      clobber either key. Don't widen this to disable both. */}
                  <div className="flex shrink-0 items-center gap-4">
                    <div className="flex items-center gap-2">
                      {/* Reserved slot — a spinner that appears from nothing
                          shifts BOTH switches sideways mid-toggle. */}
                      <span className="flex h-3 w-3 shrink-0 items-center justify-center">
                        {rowPending && (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden="true" />
                        )}
                      </span>
                      <Label
                        htmlFor={switchId}
                        className="whitespace-nowrap text-xs font-normal text-muted-foreground"
                      >
                        Garments
                      </Label>
                      <Switch
                        id={switchId}
                        checked={enabled}
                        onCheckedChange={(next) =>
                          setPreprintMutation.mutate({ locationId: location.id, enabled: next })
                        }
                        disabled={rowPending}
                        // Accessible name must contain the visible label
                        // ("Garments") — WCAG 2.5.3 Label in Name.
                        aria-label={`Garments, pre-printing at ${location.name}`}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="flex h-3 w-3 shrink-0 items-center justify-center">
                        {filmRowPending && (
                          <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden="true" />
                        )}
                      </span>
                      <Label
                        htmlFor={filmSwitchId}
                        className="whitespace-nowrap text-xs font-normal text-muted-foreground"
                      >
                        Films
                      </Label>
                      <Switch
                        id={filmSwitchId}
                        checked={filmEnabled}
                        onCheckedChange={(next) =>
                          setFilmPreprintMutation.mutate({ locationId: location.id, enabled: next })
                        }
                        disabled={filmRowPending}
                        aria-label={`Films, stock film pre-printing at ${location.name}`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Info about IMS locations (mirrors the sibling sections' footer note) */}
      {!isLoading && !hasError && locations.length > 0 && (
        <p className="pt-2 text-xs text-muted-foreground">
          To add or remove locations, go to{' '}
          <a href="/settings?tab=locations" className="underline hover:text-foreground">
            IMS Settings - Locations
          </a>
        </p>
      )}

      {/* ARIA live region — shared by both switches, text names the capability */}
      <div role="status" aria-live="polite" className="sr-only">
        {liveMessage}
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <CardTitle className="text-lg">Pre-printing</CardTitle>
        </div>
        <CardDescription>
          Which locations may pre-print garments, add pre-printed stock, or print stock films.
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
